import React, {useCallback} from 'react';
import {View, FlatList, StyleSheet, TouchableOpacity} from 'react-native';
import {Text, Chip} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import GradientHeader from '../../components/common/GradientHeader';
import {colors, spacing} from '../../theme';

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

const parseDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return {day: String(d.getDate()), month: MONTHS[d.getMonth()]};
};

const StatusChip = ({status, t}) => (
  <Chip
    style={[styles.statusChip, {backgroundColor: (colors.status[status] || colors.info) + '20'}]}
    textStyle={styles.statusChipText}>
    <Text style={{fontSize: 13, color: '#000', marginTop: 3}}>{t(`order.status.${status}`)}</Text>
  </Chip>
);

const getAmountColor = (paymentStatus) => {
  if (paymentStatus === 'PAID') return colors.success;
  if (paymentStatus === 'UNPAID') return colors.error;
  if (paymentStatus === 'PARTIALLY_PAID') return colors.warning;
  return colors.text;
};

const OrderSearchResultsScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const results = route.params?.results || [];

  const renderOrder = useCallback(({item}) => {
    const date = parseDate(item.deliveryDate);
    const amountColor = getAmountColor(item.paymentStatus);
    return (
      <TouchableOpacity
        activeOpacity={0.7}
        style={styles.row}
        onPress={() => navigation.navigate('OrderView', {orderId: item.id})}>
        <View style={styles.dateCol}>
          {date ? (
            <>
              <Text style={styles.dateDay}>{date.day}</Text>
              <Text style={styles.dateMonth}>{date.month}</Text>
            </>
          ) : (
            <Text style={styles.datePlaceholder}>—</Text>
          )}
        </View>
        <View style={styles.detailsCol}>
          <Text style={styles.orderNumber}>{item.orderNumber}</Text>
          <Text style={styles.customerName} numberOfLines={1}>{item.customerName}</Text>
          <View style={styles.statusRow}>
            <StatusChip status={item.status} t={t} />
            {item.urgent && <Icon name="flag" size={18} color={colors.error} style={{marginLeft: 6}} />}
          </View>
        </View>
        <View style={styles.amountCol}>
          <Text style={[styles.balanceAmount, {color: amountColor}]}>
            {'\u20B9'}{Number(item.balanceAmount || 0).toLocaleString()}
          </Text>
        </View>
      </TouchableOpacity>
    );
  }, [navigation, t]);

  return (
    <View style={styles.container}>
      <GradientHeader title={t('order.searchResults')} onBack={() => navigation.goBack()} />

      <FlatList
        data={results}
        renderItem={renderOrder}
        keyExtractor={item => String(item.id)}
        contentContainerStyle={styles.listContent}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Text variant="titleMedium" style={styles.emptyTitle}>{t('order.noResults')}</Text>
          </View>
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  listContent: {paddingBottom: 80},
  row: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: spacing.sm + 3, paddingHorizontal: spacing.md,
    backgroundColor: colors.surface,
    borderBottomWidth: 1, borderBottomColor: colors.divider,
  },
  dateCol: {
    width: '20%', alignItems: 'center', justifyContent: 'center',
  },
  dateDay: {fontSize: 24, fontWeight: '400', color: colors.text, lineHeight: 28},
  dateMonth: {fontSize: 11, color: colors.textSecondary, fontWeight: '600', letterSpacing: 1},
  datePlaceholder: {fontSize: 24, color: colors.textLight},
  detailsCol: {flex: 1, paddingHorizontal: spacing.sm},
  orderNumber: {fontWeight: 'bold', color: colors.primary, fontSize: 14},
  customerName: {color: colors.text, fontSize: 13, marginVertical: 2},
  statusRow: {flexDirection: 'row', alignItems: 'center', marginTop: 2},
  statusChip: {paddingVertical: 0, height: 24},
  statusChipText: {fontSize: 14, color: colors.text},
  amountCol: {alignItems: 'flex-end', minWidth: 80},
  balanceAmount: {fontSize: 14, fontWeight: '700'},
  emptyContainer: {flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: spacing.xxl * 2},
  emptyTitle: {color: colors.textSecondary},
});

export default OrderSearchResultsScreen;
